import { transliterate as hebrewTransliterate } from 'hebrew-transliteration'
import { Language } from './../components/utils/Languages'
// Lemma information from Marble dictionaries.

import _debug from 'debug'
import _ from 'underscore'

import routePrefix from '../components/app/routePrefix'
import { maybeThrowErrorMessage, retriableFetch } from '../models3/API'
import { getOriginOrBaseUrl } from '../components/utils/LocationHistory'
import { Root } from '../models3/Root'
import { fmt, s } from '../components/utils/Fmt';
import { RefRange, RefRangeMap } from './RefRange'
import { Project } from '../models3/ProjectModels'

const log = _debug('sltt:Lemmas')

// Language specific definition for a LexMeaning
export class LexDefinition {
    public languageCode: string // en, sp, fr
    public definitionShort: string
    public comments: string
    public glosses: string

    /*
        {
            "languageCode": "en",
            "definitionShort": "= {L:threshing-floor<SDBH:גֹּרֶן>}, ◄ near river {L:Jordan<SDBH:יַרְדֵּן>}...",
            "glosses": "threshing floor of Atad"
        },
     */
    constructor (_lexDefinition: {
        languageCode: string, // "en",
        definitionShort: string, // "first in a series involving time, space, or set",
        glosses: string, // "first",
        comments: string, // "Occurring only in titles of NT writings.'"
    }) {
        this.languageCode = _lexDefinition.languageCode || ''
        this.definitionShort = _lexDefinition.definitionShort || ''
        this.comments = _lexDefinition.comments || ''
        this.glosses = _lexDefinition.glosses || ''
    }
}

export interface DefinitionPart {
    text: string,
    link: string, // e.g SDBH:<lemma>, '' if no link
}

/* The following internal interface defines the structure of the lexMeaning items in
 * in the sdbgs.json and sdbhs.json files.
 */

interface _ILexMeaning {
    id: string, // "000001001001000",
    definitions: {
        languageCode: string, // "en",
        definitionShort: string, // "first in a series involving time, space, or set",
        glosses: string, // "first",
        comments: string, // "Occurring only in titles of NT writings.'"
    }[],
    entryCode: string, // "60.46",
    references: string, // "046001000|052001000|054001000|060001000|062001000"
}

// Information for a meaning (sense) of a Greek/Hebrew word
export class LexMeaning {
    public id: string   // e.g. "sdbh000002001001000"
    public entryCode: string // "60.46",
    public lexicalLink: string    // e.g. "SDBH:<Hebrew>:000001"
    public definitions: LexDefinition[]
    public references: string[] // bbbcccvvv, where this meaning is used
    public lemmaString: string  // e.g. σάββατον (from MarbleLemma)
    public senseNumber: number // 1..
    public isHebrew: boolean

    private static readonly SENSE_INDEX_START = 13
    private static readonly SENSE_INDEX_END = 15

    
    constructor(prefix: string, lemmaString: string, _lexMeaning: _ILexMeaning, isHebrew: boolean) {
        this.id = prefix + _lexMeaning.id
        this.lemmaString = lemmaString
        this.entryCode = _lexMeaning.entryCode
        
        let senseNumberString = this.id.slice(LexMeaning.SENSE_INDEX_START, LexMeaning.SENSE_INDEX_END + 1)
        let senseNumber = parseInt(senseNumberString)
        if (isNaN(senseNumber)) {
            senseNumber = 0
        }
        this.senseNumber = senseNumber
        let lexicalLinkSense = '000' + (senseNumber-1).toString().padStart(3, '0')
        
        const TAG_INDEX_END = 3
        let tag = this.id.slice(0, TAG_INDEX_END + 1).toUpperCase()
        this.lexicalLink = tag + ':' + lemmaString + ':' + lexicalLinkSense

        this.definitions = _lexMeaning.definitions.map((d: any) => new LexDefinition(d))
        this.references = (_lexMeaning.references && _lexMeaning.references.split('|')) || []

        this.isHebrew = isHebrew
    }

    dbg() {
        let { id, lexicalLink, definitions, references, lemmaString, senseNumber, isHebrew } = this
        return { id, lexicalLink, definitions, references, lemmaString, senseNumber, isHebrew }
    }

    senseName(transliterate: boolean) {
        let { lemmaString, senseNumber, isHebrew } = this
        if (transliterate) {
            return `${transliterateString(lemmaString, isHebrew) }-${senseNumber}`
        }

        return `${lemmaString}-${senseNumber}`
    }

    getDefinition(uiLanguage: string) {
        if (uiLanguage === 'es') uiLanguage = 'sp'

        let definition = this.definitions.find(def => def.languageCode === uiLanguage)
        
        if (!definition) {
            definition = this.definitions.find(def => def.languageCode === 'en')
        }

        if (!definition && this.definitions.length > 0) {
            definition = this.definitions[0]
        }

        return definition
    }

    glosses(uiLanguage: string) {
        let definition = this.getDefinition(uiLanguage)
        return definition?.glosses ?? ''
    }

    /**
     * Split shortDefinition field into user text and an 
     * optional link that can looked up in the keyterms.
     */
    splitDefinitionShort(uiLanguage: string): DefinitionPart[] {
        const definition = this.getDefinition(uiLanguage)

        // "(literally: one who has been anointed); see {D:93.387})"
        const parts = definition?.definitionShort.split(/(\{.*?\})/) ?? []

        const definitionParts = parts.map((part, i) => {
            if (i % 2 === 0) {
                return {text: part, link: ''}
            }

            let link = part.slice(1,-1)  // strip off '{' and '}'
            link = link.replace(/^\{/, '') // remove erroneous extra { found in some links

            const { termId, text } = MarbleLemmas.getByLink(link)
            return {text, link: termId ? link : ''}
        })

        return definitionParts
    }

    getComments(uiLanguage: string, project: Project) {
        const definition = this.getDefinition(uiLanguage)
        if (!definition) return ''

        const parts = definition.comments.split(/(\{S:.*?\})/)
        for (let i=1; i<parts.length; i=i+2) {
            const ref = parts[i].slice(3,12) // ref = 'BBBCCCVVV'
            const refRange = new RefRange(ref, ref)
            parts[i] = RefRange.refRangesToDisplay([refRange], project)
        }

        return parts.join('')
    }

    /** Get which meaning this is in relation to the lemma. Starts at 1.
     * @throws if cannot get number
     */
    getMeaningNumber() {
        let meaningNumber = parseInt(this.id.slice(LexMeaning.SENSE_INDEX_START, LexMeaning.SENSE_INDEX_END + 1))
        if (isNaN(meaningNumber) || !isFinite(meaningNumber)) {
            throw new Error()
        }
        return meaningNumber
    }

    static getMeaningNumberFromLexicalLink(lexicalLink: string) {
        let parts = lexicalLink.split(':')
        let termIndex = -1
        if (parts.length >= 3) {
            let _termIndex = parts[2]
            termIndex = parseInt(_termIndex)
            if (isNaN(termIndex) || !isFinite(termIndex)) {
                termIndex = -1
            }
        }
        return termIndex + 1
    }
}

// I believe (altho the truth is lost in the depths of time) that this is a list of lemmas that occur so frequently
// that trying to track links to them is not useful to the translator.
export const ignoredLemmas = new Set(`διά ἀλλά γάρ γίνομαι δέ δύναμαι ἑαυτοῦ ἐγώ εἰ εἰμί εἰς ἐκεῖνος ἐν ἤ ἵνα καί μή ὁ ὅς ὅτι οὐ οὖν οὗτος πᾶς ποιέω πολύς πρός σύ τίς τὶς ὡς אֹו אֶחָד אַחַר אַיִן אֶל אַל אֵלֶּה אִם אָנֹכִי אַרְבַּע אֲשֶׁר אֵת אַתָּה אַתֶּם בּ בַּיִן גַּם דָּבָר דִּי הוּא הִיא היה הלך הֵם זֶה זֹה חָמֵשׁ כֹּה כִּי כֵּן לֹא מְאֹד מֵאָה מָה מִי סָבִיב עַד עֹוד עַל עִם עֶשֶׂר עָשָׂר רַב שֶׁבַע שָׁלֹשׁ שָׁם שְׁנַיִם שֵׁשׁ תָּוֶךְ תַּחַת
`.split(' '))

export class MarbleLemma {
    public lemmaString: string   // e.g. "Ἀαρών", "אָטָד"
    public id: string      // e.g. "sdbh000002"
    public meanings: LexMeaning[] = []

    /* _lemma
        {
            "lexMeanings": [
                {
                    "id": "000309002001000",
                    "definitions": [
                        {
                            "languageCode": "en",
                            "definitionShort": "= {L:threshing-floor<SDBH:גֹּרֶן>}, ◄ near river {L:Jordan<SDBH:יַרְדֵּן>}...",
                            "glosses": "threshing floor of Atad"
                        },
                        ...
                    ],
                    "references": "001050010|001050011"
                }
            ],
            "id": "000309002000000",
            "lemma": "אָטָד"
        }
     */
    constructor (prefix: string /*sdbg, sdbh*/, _lemma: any) {
        this.lemmaString = _lemma.lemma
        this.id = prefix + _lemma.id.slice(0,6)
    }

    addMeanings(prefix: string, _lemma: any) {
        let isHebrew = this.id.startsWith('sdbh')

        for (let _meaning of _lemma.lexMeanings) {
            if (ignoredLemmas.has(this.lemmaString)) continue

            let meaning = new LexMeaning(prefix, this.lemmaString, _meaning, isHebrew)
            MarbleLemmas.meaningsDict.set(meaning.lexicalLink, meaning)

            if (meaning.references.length >= 200) {
                // log('!!!', meaning.lexicalLink, meaning.glosses('English'))
            }

            MarbleLemmas.refRangesToMeanings.addBbbvvvs(meaning, meaning.references)

            this.meanings.push(meaning)
        }
    }

    // If a video has been record for this biblical term return
    // its url, otherwise ''
    // signVideoUrl(rt: Root) {
    //     let signVideo = rt.project.signs[`sign/${this.id}`]
    //     return signVideo ? signVideo.url : ''
    // }

    // async deleteSignVideo(rt: Root) {
    //     log('deleteSignVideo', this.id)

    //     let signVideo = rt.project.signs[`sign/${this.id}`]
    //     if (signVideo) {
    //         log('found')
    //         await signVideo.delete()
    //     }
    // }
}

export class MarbleLemmas {
    // Lemmas are present for each ref (bbbcccvvv)
    public static lemmasDict: { [lemmaString: string]: MarbleLemma } = {}
    public static meaningsDict = new Map<string, LexMeaning>()

    public static refRangesToMeanings = new RefRangeMap<LexMeaning>()
    // get(RefRange[]) => LexMeaning[]

    static parseLink(link: string) {
        // L:Dead Sea<SDBH:יָם־הַמֶּלַח>
        // L:ἀγαθοποιέω<SDBG:ἀγαθοποιέω:000000>
        const match = link.match(/L:(?<text>[^<]+)<(?<termId>\w\w\w\w:[^:>]+)/)
        if (match) {
            return { 
                termId: match.groups!.termId, 
                entryCode: '', 
                text: match.groups!.text
            }
        }
        
        // D:23.125
        const match2 = link.match(/D:(?<entryCode>\d+\.\d+)/)
        if (match2) {
            return { 
                termId: '', 
                entryCode: match2.groups!.entryCode, 
                text: link
            }
        }

        // Don't know how to follow these links so ignore them
        if (link.startsWith('S:') || link.startsWith('N:')) {
            return { 
                termId: '', 
                entryCode: '', 
                text: ''
            }
        }

        log('### invalid link format', link )
        return { termId: '', entryCode: '', text: link}
    }

    static getByLink(link: string) {
        let { termId, entryCode, text} = MarbleLemmas.parseLink(link)

        if (termId) {
            if (MarbleLemmas.get(termId)) {
                return { termId, meaning: null, text}
            }
        }
        
        if (entryCode) {
            for (let lemma of Object.values(this.lemmasDict)) {
                let meaning = lemma.meanings.find(m => m.entryCode === entryCode)
                if (meaning) {
                    termId = meaning.lexicalLink
                    log('getByLink', link, meaning )
                    return { termId, meaning, text }
                }
            }
        }

        log('getByLink NOT FOUND', link )
        return { termId: '', meaning: null, text }
    }

    static get(termId: string, doLog?: boolean) {
        // termId = "SDBG:γένεσις:000001"
        const lemmaString = termId.split(':')[1]
        const lemma = this.lemmasDict[lemmaString]
        log('get', fmt({ termId, found: lemma ? true : false }))
        
        console.groupCollapsed('lemma')
        log('get lemma', fmt({ lemma }))
        console.groupEnd()

        return lemma
    }

    // Fetch Greek and Hebrew terms info and add to table
    static async fetch() {
        await MarbleLemmas.fetchLemmas('sdbg')
        await MarbleLemmas.fetchLemmas('sdbh')
    }

    // Fetch sdbgs or sdbhs data from S3.
    static async fetchLemmas(prefix: string /* 'sdbg' or 'sdbh' */) {
        const origin = getOriginOrBaseUrl()
        let url = `${origin}/data/${prefix}s.json`
        
        let response = await retriableFetch(url, {})
        await maybeThrowErrorMessage(response)

        let jsons: any[] = await response.json()
        log('fetchLemmas', fmt({url, count: jsons.length}))

        for (let json of jsons) {
            let newLemma = new MarbleLemma(prefix, json)
            let _lemma = this.lemmasDict[newLemma.lemmaString]
            if (!_lemma) {
                this.lemmasDict[newLemma.lemmaString] = newLemma
                _lemma = newLemma
            }

            _lemma.addMeanings(prefix, json)
        }
    }
}

export function transliterateString(lemmaString: string, isHebrew: boolean) {
    return isHebrew ? hebrewTransliterate(lemmaString) : greekTransliterate(lemmaString)
}

function greekTransliterate(lemma: string) {
    lemma = lemma.normalize('NFD') // separate letters from diacritics
    lemma = lemma.replace(/[\u0300-\u036f]/g, "")  // remove diacritics

    let table = `α a
β b
γγ nγ
γκ nκ
γξ nξ
γχ nχ
γ g
δ d
ε e
ζ z
η ē
θ th
ι i
κ k
λ l
μ m
ν n
ξ x
ο o
π p
ῥ rh
ρ r
σ s 
ς s
τ t
aυ au
eυ eu
ēυ ēu
oυ ou
υi ui
υ y
φ ph
χ ch
ψ ps
ω ō
Α A
Β B
Γ G
Δ D
Ε E
Ζ Z
Η Ē
Θ Th
Ι I
Κ K
Λ L
Μ M
Ν N
Ξ X
Ο O
Π P
Ρ R
Σ S
Τ T
Υ Y
Φ Ph
Χ Ch
Ψ Ps
Ω Ō
῾ h`

    let trans = lemma
    table.split("\n").forEach(item => {
        let parts = item.trim().split(' ')
        while (trans.includes(parts[0])) {
            trans = trans.replace(parts[0], parts[1])
        }
    })

    return lemma === trans ? '' : trans
}
